import { useRef, useState, type KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import { Input } from 'components/ui/input';
import type { AddressSuggestion } from 'api/geocodingApi';
import { useAddressSuggestions } from 'hooks/useAddressSuggestions';
import { usePlaceAddress } from 'hooks/usePlaceAddress';
import { cn } from 'lib/utils';
import type { AddressDraft } from './validation';

interface AddressAutocompleteProps {
  id: string;
  value: string;
  country: string | null;
  readOnly?: boolean;
  placeholder?: string;
  /** Styles the input, so the grouped address control can drop its own border. */
  className?: string;
  onType: (value: string) => void;
  onPick: (patch: Partial<AddressDraft>, placeId: string) => void;
}

/**
 * The address line with Places suggestions (L2c). It is a plain input first: with no key, a
 * failing provider or in read-only, nothing but the input shows and nothing blames anyone (AC4).
 */
const AddressAutocomplete = ({
  id,
  value,
  country,
  readOnly = false,
  placeholder,
  className,
  onType,
  onPick,
}: AddressAutocompleteProps) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const { state, suggestions } = useAddressSuggestions(value, country, open && !readOnly);
  const resolvePlace = usePlaceAddress();
  // The place lookup resolves after later renders: it must act on the props of now, not of the click.
  const latest = useRef({ value, onPick });
  latest.current = { value, onPick };

  if (readOnly) return <Input id={id} value={value} placeholder={placeholder} className={className} disabled readOnly />;

  const listId = `${id}-suggestions`;
  const showList = open && state === 'ok';
  const hasOptions = showList && suggestions.length > 0;
  const activeOption = hasOptions ? suggestions[active] : undefined;
  const optionId = (index: number) => `${listId}-${index}`;

  const pick = async ({ placeId, label }: AddressSuggestion) => {
    setOpen(false);
    onType(label);
    const address = await resolvePlace(placeId).catch(() => null);
    // No address, or typed over since: the label stays as a manual entry, with no placeId.
    if (!address || latest.current.value !== label) return;
    const patch: Partial<AddressDraft> = { line1: address.line1 ?? label };
    if (address.postalCode) patch.postalCode = address.postalCode;
    if (address.city) patch.city = address.city;
    latest.current.onPick(patch, placeId);
  };

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Escape' && open) {
      event.preventDefault();
      setOpen(false);
    } else if (event.key === 'ArrowDown') {
      event.preventDefault();
      setOpen(true);
      if (hasOptions) setActive((index) => Math.min(index + 1, suggestions.length - 1));
    } else if (event.key === 'ArrowUp' && hasOptions) {
      event.preventDefault();
      setActive((index) => Math.max(index - 1, 0));
    } else if (event.key === 'Enter' && activeOption) {
      event.preventDefault(); // Picking a suggestion must not submit the record.
      pick(activeOption);
    }
  };

  return (
    <div className="relative">
      <Input
        id={id}
        value={value}
        autoComplete="off"
        role="combobox"
        aria-autocomplete="list"
        aria-expanded={showList}
        aria-controls={hasOptions ? listId : undefined}
        aria-activedescendant={activeOption ? optionId(active) : undefined}
        placeholder={placeholder}
        className={cn(className, state === 'loading' && 'pr-8')}
        onChange={(event) => {
          onType(event.target.value);
          setOpen(true);
          setActive(-1);
        }}
        onKeyDown={onKeyDown}
        onBlur={() => setOpen(false)}
      />
      {state === 'loading' && (
        <span className="pointer-events-none absolute inset-y-0 right-2.5 flex items-center text-zinc-400 dark:text-zinc-500">
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
          <span className="sr-only">{t('Loading…')}</span>
        </span>
      )}
      {showList && (
        <div className="absolute z-20 mt-1 w-full overflow-hidden rounded-md border border-zinc-200 bg-white shadow-md dark:border-zinc-800 dark:bg-zinc-950">
          {hasOptions ? (
            <ul id={listId} role="listbox" className="max-h-60 overflow-auto py-1">
              {suggestions.map((suggestion, index) => (
                <li
                  key={suggestion.placeId}
                  id={optionId(index)}
                  role="option"
                  aria-selected={index === active}
                  className={cn(
                    'cursor-pointer px-3 py-2 text-sm text-zinc-900 dark:text-zinc-50',
                    index === active && 'bg-zinc-100 dark:bg-zinc-800'
                  )}
                  onMouseDown={(event) => event.preventDefault()} // keep focus: blur would close the list first
                  onMouseEnter={() => setActive(index)}
                  onClick={() => pick(suggestion)}
                >
                  {suggestion.label}
                </li>
              ))}
            </ul>
          ) : (
            <p role="status" className="px-3 py-2 text-xs text-zinc-500 dark:text-zinc-400">
              {t('No address found')}
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default AddressAutocomplete;
